import { addPence, pence, subPence, ZERO_PENCE } from '@cf/shared';
import { assertReviewed } from './review.js';
import { clampTo, excessOver, minPence, mulBps } from './bps.js';
export const EMPTY_POOL = Object.freeze({ units: 0, cost: ZERO_PENCE });
export function poolAcquire(pool, units, cost) {
    if (!(units > 0) || !Number.isFinite(units)) {
        throw new RangeError(`Acquisition units must be positive and finite: ${units}`);
    }
    if (!Number.isSafeInteger(cost) || cost < 0) {
        throw new RangeError('Acquisition cost must be a non-negative integer of pence');
    }
    return Object.freeze({ units: pool.units + units, cost: addPence(pool.cost, pence(cost)) });
}
export function poolDispose(pool, units, proceeds) {
    if (!(units > 0) || units > pool.units) {
        throw new RangeError(`Cannot dispose of ${units} units from a pool of ${pool.units}`);
    }
    if (!Number.isSafeInteger(proceeds) || proceeds < 0) {
        throw new RangeError('Disposal proceeds must be a non-negative integer of pence');
    }
    // Whole-pool disposal carries the exact remaining cost (no rounding drift).
    const allowableCost = units === pool.units ? pool.cost : pence(Math.trunc((pool.cost * units) / pool.units));
    return {
        pool: Object.freeze({ units: pool.units - units, cost: subPence(pool.cost, allowableCost) }),
        allowableCost,
        gain: subPence(pence(proceeds), allowableCost),
    };
}
export function applyBroughtForwardLosses(grossGains, broughtForwardLosses, annualExemptAmount) {
    if (!Number.isSafeInteger(grossGains) || grossGains < 0) {
        throw new RangeError('grossGains must be a non-negative integer of pence');
    }
    if (!Number.isSafeInteger(broughtForwardLosses) || broughtForwardLosses < 0) {
        throw new RangeError('broughtForwardLosses must be a non-negative integer of pence');
    }
    // Only the gains ABOVE the AEA can be reduced by brought-forward losses.
    const gainsAboveAea = excessOver(grossGains, annualExemptAmount);
    const used = minPence(gainsAboveAea, broughtForwardLosses);
    return {
        netGains: subPence(pence(grossGains), used),
        lossesCarriedForward: subPence(broughtForwardLosses, used),
    };
}
export function capitalGainsTax(rules, input) {
    assertReviewed(rules);
    const r = rules.capitalGains;
    const lowerRate = input.residential && r.residentialLowerRateBps !== undefined
        ? r.residentialLowerRateBps
        : r.lowerRateBps;
    const higherRate = input.residential && r.residentialHigherRateBps !== undefined
        ? r.residentialHigherRateBps
        : r.higherRateBps;
    const used = input.annualExemptAmountUsed ?? ZERO_PENCE;
    const exemptLeft = excessOver(r.annualExemptAmount, used);
    const taxableGain = excessOver(pence(Math.max(0, input.netGains)), exemptLeft);
    const atLower = minPence(taxableGain, clampTo(input.basicRateHeadroom, taxableGain));
    const atHigher = subPence(taxableGain, atLower);
    const taxAtLowerRate = mulBps(atLower, lowerRate);
    const taxAtHigherRate = mulBps(atHigher, higherRate);
    return {
        taxableGain,
        taxAtLowerRate,
        taxAtHigherRate,
        totalTax: addPence(taxAtLowerRate, taxAtHigherRate),
    };
}
//# sourceMappingURL=capital-gains.js.map